/**
 * Theme store — light / dark / system appearance preference.
 *
 * The preference is a *device-local* choice (like Claude's), so it lives in
 * localStorage under `cowork-theme` — never round-tripped to the backend. The
 * actual switch is a single `.dark` class on <html>; all theming flows from the
 * CSS-variable tokens in index.css (see docs/DESIGN.md "Dark mode"). A no-FOUC
 * inline script in index.html applies the same class before React mounts; this
 * store keeps it in sync, reacts to OS-level changes while on "system", and
 * exposes the current mode to the settings UI.
 */

import { create } from "zustand";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "cowork-theme";

const prefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)");

/** OS scheme the native shell detected and injected via `?native_scheme=`
 *  (index.html stashes it here). Undefined in a real browser, where
 *  `matchMedia` is reliable. See cowork/ui/system_theme.py for why. */
function nativeScheme(): ResolvedTheme | undefined {
  const ns = (window as unknown as { __NATIVE_SCHEME__?: string }).__NATIVE_SCHEME__;
  return ns === "dark" || ns === "light" ? ns : undefined;
}

/** Whether "System" should resolve to dark — trusting the native shell's
 *  detection over `matchMedia`, which the embedded webviews mis-report. */
function systemPrefersDark(): boolean {
  return nativeScheme() ? nativeScheme() === "dark" : prefersDark().matches;
}

/** Resolve a mode to a concrete theme, consulting the OS for "system". */
function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode;
}

/** Toggle the `.dark` class on <html> to match `mode`; returns the resolved theme. */
function applyTheme(mode: ThemeMode): ResolvedTheme {
  const resolved = resolve(mode);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  return resolved;
}

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* localStorage unavailable (e.g. pywebview quirk) — fall back to system */
  }
  return "system";
}

/** Briefly enable a uniform color cross-fade around a theme switch. */
function animateSwitch() {
  const root = document.documentElement;
  root.classList.add("theme-anim");
  window.setTimeout(() => root.classList.remove("theme-anim"), 260);
}

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  /** Persist + apply a new appearance preference. */
  setMode: (mode: ThemeMode) => void;
  /** Bind the OS-change listener and reconcile the class on startup. */
  init: () => void;
}

const initialMode = readStored();
let listenerBound = false;

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: initialMode,
  resolved: resolve(initialMode),

  setMode: (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore — the class still applies for this session */
    }
    animateSwitch();
    set({ mode, resolved: applyTheme(mode) });
  },

  init: () => {
    // Re-apply on mount (idempotent with the no-FOUC inline script) so the
    // store's resolved value and the DOM class can never drift.
    set({ resolved: applyTheme(get().mode) });

    if (listenerBound) return;
    listenerBound = true;
    const mql = prefersDark();
    const onChange = () => {
      // Only OS-driven while the user is on "system".
      if (get().mode !== "system") return;
      animateSwitch();
      set({ resolved: applyTheme("system") });
    };
    // Safari <14 / older WebKit only support the deprecated addListener.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
  },
}));
